import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';
import 'react-datepicker/dist/react-datepicker.css';
import { X, Save } from 'lucide-react';
import { customSelectStyles } from '../../config/selectStyles';

registerLocale('de', de);

// 30-minute time options
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        TIME_OPTIONS.push(`${hh}:${mm}`);
    }
}

const TRIGGER_OPTIONS = [
    { value: 'welcome', label: 'Willkommen (bei Newsletter-Bestätigung)' },
    { value: 'job_followup', label: 'Verzögerung nach Job-Abschluss' },
    { value: 'date_based', label: 'Datum-basiert (jährlich)' },
    { value: 'birthday', label: 'Geburtstag' },
];

const RECIPIENT_OPTIONS = [
    { value: 'group', label: 'Bestätigte Abonnenten der gewählten Newsletter-Gruppe' },
    { value: 'all', label: 'Alle Kontakte mit E-Mail-Adresse' },
];

const fieldStyle = {
    marginBottom: '1.25rem',
};

const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--text-primary, #2c3e50)',
    marginBottom: '0.375rem',
};

const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    fontSize: '0.9rem',
    color: 'var(--text-primary, #2c3e50)',
    background: 'var(--bg-card, #ffffff)',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
};

const hintStyle = {
    fontSize: '0.8rem',
    color: 'var(--text-secondary, #64748b)',
    marginTop: '0.25rem',
};

const AutomationEditor = ({ automation, onClose, onSaved }) => {
    const isEdit = !!automation;

    const [name, setName] = useState(automation?.name || '');
    const [triggerType, setTriggerType] = useState(
        automation ? TRIGGER_OPTIONS.find(o => o.value === automation.trigger_type) : null
    );
    const [delayDays, setDelayDays] = useState(automation?.delay_days || 7);
    const [sendDate, setSendDate] = useState(
        automation?.send_date ? new Date(automation.send_date) : null
    );
    const [sendTime, setSendTime] = useState(automation?.send_time?.slice(0, 5) || '09:00');
    const [campaignOption, setCampaignOption] = useState(null);
    const [groupOption, setGroupOption] = useState(null);
    const [recipientType, setRecipientType] = useState(
        automation?.group_id ? 'group' : (automation ? 'all' : 'group')
    );

    const [campaigns, setCampaigns] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const savingRef = useRef(false);
    const backdropMouseDownRef = useRef(false);

    // Kampagnen laden — gefiltert nach Gruppe (oder alle wenn keine Gruppe gewählt)
    const loadCampaigns = useCallback(async (groupId, preSelectCampaignId = null) => {
        setLoadingCampaigns(true);
        setCampaigns([]);
        try {
            const url = groupId
                ? `index.php/newsletter/getCampaigns?group_id=${groupId}`
                : 'index.php/newsletter/getCampaigns';
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                const list = Array.isArray(data) ? data : (data.campaigns || []);
                const options = list
                    .filter(c => c.status !== 'sent')
                    .map(c => ({ value: c.campaign_id, label: c.name }));
                setCampaigns(options);

                if (preSelectCampaignId) {
                    const found = options.find(o => String(o.value) === String(preSelectCampaignId));
                    if (found) setCampaignOption(found);
                }
            }
        } catch (err) {
            console.error('Error loading campaigns:', err);
        } finally {
            setLoadingCampaigns(false);
        }
    }, []);

    // Gruppen laden
    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetch('index.php/groups/getGroups');
                if (response.ok) {
                    const data = await response.json();
                    const list = Array.isArray(data) ? data : (data.groups || []);
                    const options = list.map(g => ({ value: g.group_id, label: g.group_name || g.name }));
                    setGroups(options);

                    // Pre-select Gruppe bei Bearbeitung → löst danach Kampagnen-Load aus
                    if (automation?.group_id) {
                        const found = options.find(o => String(o.value) === String(automation.group_id));
                        if (found) {
                            setGroupOption(found);
                            // Kampagnen für diese Gruppe laden und Kampagne vorauswählen
                            loadCampaigns(found.value, automation?.campaign_id);
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading groups:', err);
            } finally {
                setLoadingGroups(false);
            }
        };
        load();
    }, []);

    // Wenn Gruppe gewechselt wird: Kampagne zurücksetzen + neu laden
    const handleGroupChange = (opt) => {
        setGroupOption(opt);
        setCampaignOption(null);
        setCampaigns([]);
        setErrors(e => ({ ...e, group: null, campaign: null }));
        if (opt) {
            loadCampaigns(opt.value);
        }
    };

    const validate = () => {
        const errs = {};
        if (!name.trim()) errs.name = 'Name ist erforderlich';
        if (!triggerType) errs.triggerType = 'Trigger-Typ ist erforderlich';
        if (!campaignOption) errs.campaign = 'Bitte eine Kampagne auswählen';
        if (triggerType?.value === 'welcome' && !groupOption) {
            errs.group = 'Bitte eine Newsletter-Gruppe für den Willkommens-Trigger auswählen';
        }
        if (triggerType?.value === 'job_followup' && (!delayDays || delayDays < 1)) {
            errs.delayDays = 'Bitte eine gültige Anzahl Tage eingeben (min. 1)';
        }
        if (triggerType?.value === 'date_based' && !sendDate) {
            errs.sendDate = 'Bitte ein Datum auswählen';
        }
        if (recipientType === 'group' && !groupOption && triggerType?.value !== 'welcome') {
            errs.group = 'Bitte eine Newsletter-Gruppe auswählen';
        }
        return errs;
    };

    const handleSave = async () => {
        if (savingRef.current) return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setErrors({});
        savingRef.current = true;
        setSaving(true);

        const payload = {
            name: name.trim(),
            trigger_type: triggerType.value,
            campaign_id: campaignOption?.value || '',
            group_id: (recipientType === 'group' && groupOption) ? groupOption.value : '',
            delay_days: triggerType?.value === 'job_followup' ? delayDays : '',
            send_date: triggerType?.value === 'date_based' && sendDate
                ? sendDate.toISOString().split('T')[0]
                : '',
            send_time: sendTime,
        };

        if (isEdit) {
            payload.uuid = automation.uuid;
        }

        try {
            const endpoint = isEdit
                ? 'index.php/newsletter/updateAutomation'
                : 'index.php/newsletter/createAutomation';

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(payload),
            });

            if (response.ok) {
                onSaved();
            } else {
                setErrors({ global: 'Fehler beim Speichern. Bitte versuche es erneut.' });
            }
        } catch (err) {
            console.error('Error saving automation:', err);
            setErrors({ global: 'Netzwerkfehler. Bitte versuche es erneut.' });
        } finally {
            setSaving(false);
            savingRef.current = false;
        }
    };

    const modalOverlayStyle = {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
    };

    const modalStyle = {
        background: 'var(--bg-card, #ffffff)',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    const headerStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderRadius: '12px 12px 0 0',
    };

    const bodyStyle = {
        padding: '1.5rem',
        overflowY: 'auto',
        flex: 1,
    };

    const footerStyle = {
        padding: '1rem 1.5rem',
        borderTop: '1px solid var(--border-light, #e2e8f0)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderRadius: '0 0 12px 12px',
    };

    const errorStyle = {
        fontSize: '0.8rem',
        color: '#dc3545',
        marginTop: '0.25rem',
    };

    const sectionHeaderStyle = {
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--text-secondary, #64748b)',
        marginBottom: '1rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid var(--border-light, #e2e8f0)',
    };

    return (
        <div
            style={modalOverlayStyle}
            onMouseDown={(e) => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onMouseUp={(e) => {
                if (backdropMouseDownRef.current && e.target === e.currentTarget) {
                    onClose();
                }
                backdropMouseDownRef.current = false;
            }}
        >
            <div style={modalStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <h5 style={{ margin: 0, color: 'var(--text-primary, #2c3e50)', fontWeight: 600, fontSize: '1rem' }}>
                        {isEdit ? 'Automatisierung bearbeiten' : 'Neue Automatisierung'}
                    </h5>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--text-secondary, #64748b)', display: 'flex', padding: '0.25rem',
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={bodyStyle}>
                    {errors.global && (
                        <div style={{
                            background: '#fef2f2', border: '1px solid #fecaca',
                            borderRadius: '8px', padding: '0.75rem 1rem',
                            color: '#dc2626', fontSize: '0.875rem', marginBottom: '1.25rem',
                        }}>
                            {errors.global}
                        </div>
                    )}

                    {/* Name */}
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="z.B. Willkommens-Mail neue Abonnenten"
                            style={{ ...inputStyle, borderColor: errors.name ? '#dc3545' : 'var(--border-medium, #cbd5e1)' }}
                        />
                        {errors.name && <p style={errorStyle}>{errors.name}</p>}
                        <p style={hintStyle}>Interner Name — wird nur in der Übersicht angezeigt</p>
                    </div>

                    {/* Trigger Type */}
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Trigger-Typ *</label>
                        <Select
                            options={TRIGGER_OPTIONS}
                            value={triggerType}
                            onChange={opt => { setTriggerType(opt); setErrors(e => ({ ...e, triggerType: null })); }}
                            placeholder="Trigger auswählen..."
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            classNamePrefix="select"
                        />
                        {errors.triggerType && <p style={errorStyle}>{errors.triggerType}</p>}
                    </div>

                    {/* Conditional fields per trigger type */}
                    {triggerType?.value === 'welcome' && (
                        <div style={{
                            background: 'var(--bg-secondary, #f5f7f9)',
                            border: '1px solid var(--border-light, #e2e8f0)',
                            borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem'
                        }}>
                            <p style={sectionHeaderStyle}>Willkommens-Einstellungen</p>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Newsletter-Gruppe *</label>
                                <Select
                                    options={groups}
                                    value={groupOption}
                                    onChange={handleGroupChange}
                                    placeholder={loadingGroups ? 'Lädt...' : 'Gruppe auswählen...'}
                                    isLoading={loadingGroups}
                                    styles={customSelectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    classNamePrefix="select"
                                    isClearable
                                />
                                {errors.group && <p style={errorStyle}>{errors.group}</p>}
                                <p style={hintStyle}>Wird ausgelöst wenn ein Abonnent in dieser Gruppe bestätigt</p>
                            </div>
                        </div>
                    )}

                    {triggerType?.value === 'job_followup' && (
                        <div style={{
                            background: 'var(--bg-secondary, #f5f7f9)',
                            border: '1px solid var(--border-light, #e2e8f0)',
                            borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem'
                        }}>
                            <p style={sectionHeaderStyle}>Verzögerungs-Einstellungen</p>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Verzögerung nach Job-Abschluss *</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <input
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={delayDays}
                                        onChange={e => setDelayDays(parseInt(e.target.value) || 1)}
                                        style={{
                                            ...inputStyle,
                                            width: '100px',
                                            borderColor: errors.delayDays ? '#dc3545' : 'var(--border-medium, #cbd5e1)',
                                        }}
                                    />
                                    <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.875rem' }}>
                                        Tage nach Job-Abschluss
                                    </span>
                                </div>
                                {errors.delayDays && <p style={errorStyle}>{errors.delayDays}</p>}
                                <p style={hintStyle}>E-Mail wird X Tage nach Abschluss eines Jobs gesendet</p>
                            </div>
                        </div>
                    )}

                    {triggerType?.value === 'date_based' && (
                        <div style={{
                            background: 'var(--bg-secondary, #f5f7f9)',
                            border: '1px solid var(--border-light, #e2e8f0)',
                            borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem'
                        }}>
                            <p style={sectionHeaderStyle}>Datum-Einstellungen</p>
                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: '160px' }}>
                                    <label style={labelStyle}>Datum *</label>
                                    <DatePicker
                                        selected={sendDate}
                                        onChange={date => { setSendDate(date); setErrors(e => ({ ...e, sendDate: null })); }}
                                        dateFormat="dd.MM.yyyy"
                                        locale="de"
                                        placeholderText="TT.MM.JJJJ"
                                        customInput={
                                            <input
                                                style={{
                                                    ...inputStyle,
                                                    borderColor: errors.sendDate ? '#dc3545' : 'var(--border-medium, #cbd5e1)',
                                                }}
                                            />
                                        }
                                    />
                                    {errors.sendDate && <p style={errorStyle}>{errors.sendDate}</p>}
                                </div>
                                <div style={{ minWidth: '140px' }}>
                                    <label style={labelStyle}>Uhrzeit</label>
                                    <select
                                        value={sendTime}
                                        onChange={e => setSendTime(e.target.value)}
                                        style={inputStyle}
                                    >
                                        {TIME_OPTIONS.map(t => (
                                            <option key={t} value={t}>{t} Uhr</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <p style={{ ...hintStyle, marginTop: '0.75rem' }}>
                                Wird jährlich an diesem Datum gesendet (z.B. 01.01. für Neujahrsgrüße)
                            </p>
                        </div>
                    )}

                    {triggerType?.value === 'birthday' && (
                        <div style={{
                            background: 'var(--bg-secondary, #f5f7f9)',
                            border: '1px solid var(--border-light, #e2e8f0)',
                            borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem'
                        }}>
                            <p style={sectionHeaderStyle}>Geburtstags-Einstellungen</p>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Uhrzeit des Versands</label>
                                <select
                                    value={sendTime}
                                    onChange={e => setSendTime(e.target.value)}
                                    style={{ ...inputStyle, maxWidth: '180px' }}
                                >
                                    {TIME_OPTIONS.map(t => (
                                        <option key={t} value={t}>{t} Uhr</option>
                                    ))}
                                </select>
                                <p style={hintStyle}>
                                    Wird täglich geprüft und am Geburtstag des Kontakts gesendet (Geburtsdatum muss im CRM hinterlegt sein)
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Gruppe (für alle Trigger außer welcome, wo Gruppe schon oben steht) */}
                    {triggerType?.value !== 'welcome' && (
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Newsletter-Gruppe *</label>
                            <Select
                                options={groups}
                                value={groupOption}
                                onChange={handleGroupChange}
                                placeholder={loadingGroups ? 'Lädt...' : 'Gruppe auswählen...'}
                                isLoading={loadingGroups}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                classNamePrefix="select"
                                isClearable
                            />
                            {errors.group && <p style={errorStyle}>{errors.group}</p>}
                            <p style={hintStyle}>
                                Bestimmt welche Kampagnen zur Auswahl stehen und (je nach Empfängerkreis) wer die E-Mail erhält
                            </p>
                        </div>
                    )}

                    {/* Kampagne — nur anzeigen wenn Gruppe gewählt (oder welcome-Trigger) */}
                    {(groupOption || triggerType?.value === 'welcome') && (
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Kampagne *</label>
                            <Select
                                options={campaigns}
                                value={campaignOption}
                                onChange={opt => { setCampaignOption(opt); setErrors(e => ({ ...e, campaign: null })); }}
                                placeholder={loadingCampaigns ? 'Lädt...' : (campaigns.length === 0 ? 'Keine Entwürfe in dieser Gruppe' : 'Kampagne auswählen...')}
                                isLoading={loadingCampaigns}
                                isDisabled={loadingCampaigns}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                classNamePrefix="select"
                                isClearable
                                noOptionsMessage={() => 'Keine Kampagnen gefunden — erstelle zuerst einen Entwurf in der gewählten Gruppe'}
                            />
                            {errors.campaign && <p style={errorStyle}>{errors.campaign}</p>}
                            <p style={hintStyle}>Nur Entwürfe und geplante Kampagnen dieser Gruppe (keine bereits gesendeten)</p>
                        </div>
                    )}

                    {/* Empfängerkreis */}
                    {triggerType?.value !== 'welcome' && (
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Empfängerkreis</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {RECIPIENT_OPTIONS.map(opt => (
                                    <label
                                        key={opt.value}
                                        style={{
                                            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                            cursor: 'pointer', fontSize: '0.875rem',
                                            color: 'var(--text-primary, #2c3e50)',
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="recipientType"
                                            value={opt.value}
                                            checked={recipientType === opt.value}
                                            onChange={() => setRecipientType(opt.value)}
                                            style={{ marginTop: '2px', accentColor: 'var(--accent-slate, #5b7c8d)' }}
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                            <p style={hintStyle}>
                                Die Gruppe bestimmt weiterhin welche Kampagne verwendet wird — der Empfängerkreis legt fest wer sie bekommt
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={footerStyle}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="btn btn-outline-secondary btn-sm"
                    >
                        Abbrechen
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Speichert...
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                {isEdit ? 'Speichern' : 'Erstellen'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AutomationEditor;
